import { type Action } from 'react-sweet-state';

import {
	type ProductData,
	type RemoteActionsIndexedById,
	type RemoteActionsMetas,
	type RemoteActionsResponse,
} from '../../../../common/types';
import { type RemoteActionsStoreState } from '../../index';

import { isActionConditionSatisfied, shouldProcessRemoteAction } from './utils';

export const processRemoteActions =
	(
		productData: ProductData,
		remoteActionsResponse: RemoteActionsResponse,
	): Action<RemoteActionsStoreState> =>
	({ getState, setState }) => {
		const { lastUpdatedAt, meta } = getState();
		if (remoteActionsResponse.lastUpdatedAt <= lastUpdatedAt) {
			// data has not been updated, nothing to do
			return;
		}

		const remoteActions = remoteActionsResponse.remoteActions.reduce<RemoteActionsIndexedById>(
			(updatedRemoteActions, remoteAction) => {
				if (shouldProcessRemoteAction(remoteAction)) {
					const shouldAddAction = remoteAction.conditions.every((condition) =>
						isActionConditionSatisfied(productData, condition),
					);

					if (shouldAddAction) {
						updatedRemoteActions[remoteAction.id] = remoteAction;
					}
				}

				// no updates
				return updatedRemoteActions;
			},
			{},
		);

		setState({
			// only keep the meta for the remote actions that are still present. This is to avoid
			// keeping stale meta which would otherwise grow indefinitely.
			meta: Object.keys(remoteActions).reduce((newMeta, id) => {
				newMeta[id] = meta[id];
				return newMeta;
			}, {} as RemoteActionsMetas),
			remoteActions,
			lastUpdatedAt: remoteActionsResponse.lastUpdatedAt,
		});
	};
